/*
  <chair-recline> — interactive recline demo for chair PDPs.

  A native range input (+ preset chips) sets a recline angle; the [data-cr-rotate]
  SVG group (backrest + seated figure's torso/head + angle guide) rotates around the
  seat-back pivot. On first scroll-in it auto-eases once (upright → full recline →
  settle at the work angle); prefers-reduced-motion skips the auto-play (static at
  work angle); any user interaction cancels auto-play. Rotation uses the fractional
  angle for smoothness; the readout shows the rounded degree + a position label.

  Guarded, defer-loaded.
*/
if (!customElements.get('chair-recline')) {
  customElements.define(
    'chair-recline',
    class ChairRecline extends HTMLElement {
      connectedCallback() {
        this.slider = this.querySelector('[data-cr-slider]');
        this.rotate = this.querySelector('[data-cr-rotate]');
        this.angleOut = this.querySelector('[data-cr-angle]');
        this.posOut = this.querySelector('[data-cr-pos]');
        this.live = this.querySelector('[data-cr-live]');
        this.presets = Array.from(this.querySelectorAll('[data-cr-preset]'));
        if (!this.slider || !this.rotate) return;

        this.min = parseFloat(this.dataset.min) || 90;
        this.max = parseFloat(this.dataset.max) || 130;
        this.work = parseFloat(this.dataset.work) || 112;
        this.labels = { low: this.dataset.posLow || '', mid: this.dataset.posMid || '', high: this.dataset.posHigh || '' };
        this.PX = 313;
        this.PY = 240;
        this.userTouched = false;
        this.played = false;
        this.raf = null;

        const touch = () => { this.userTouched = true; this.stopTween(); };
        this.slider.addEventListener('pointerdown', touch);
        this.slider.addEventListener('keydown', touch);
        this.slider.addEventListener('input', () => { touch(); this.paint(parseFloat(this.slider.value), true); });
        this.presets.forEach((p) =>
          p.addEventListener('click', () => { touch(); this.tween(parseFloat(this.slider.value), parseFloat(p.dataset.crPreset), 420, true); })
        );

        this.paint(this.work, false);
        this.maybeAutoplay();
      }

      disconnectedCallback() {
        this.stopTween();
      }

      posLabel(a) {
        const f = (a - this.min) / (this.max - this.min || 1);
        return f < 0.34 ? this.labels.low : f < 0.7 ? this.labels.mid : this.labels.high;
      }

      paint(a, announce) {
        const disp = Math.round(a);
        const rot = -(a - 90);
        this.rotate.setAttribute('transform', 'rotate(' + rot.toFixed(2) + ' ' + this.PX + ' ' + this.PY + ')');
        if (this.angleOut) this.angleOut.textContent = disp + '°';
        const p = this.posLabel(disp);
        if (this.posOut) this.posOut.textContent = p;
        this.presets.forEach((b) => b.classList.toggle('is-on', parseFloat(b.dataset.crPreset) === disp));
        if (announce && this.live) this.live.textContent = disp + '°, ' + p;
      }

      setVal(a) {
        this.slider.value = Math.round(a);
        this.paint(a, false);
      }

      stopTween() {
        if (this.raf) cancelAnimationFrame(this.raf);
        this.raf = null;
      }

      tween(from, to, dur, announce, then) {
        this.stopTween();
        const self = this;
        let start = null;
        const ease = (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);
        function step(ts) {
          if (start === null) start = ts;
          const t = Math.min((ts - start) / dur, 1);
          self.setVal(from + (to - from) * ease(t));
          if (t < 1) {
            self.raf = requestAnimationFrame(step);
          } else {
            self.raf = null;
            self.paint(to, announce);
          }
        }
        this.raf = requestAnimationFrame(step);
      }

      maybeAutoplay() {
        if (this.userTouched) return;
        const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (reduced) { this.setVal(this.work); return; }
        if (!('IntersectionObserver' in window)) { this.setVal(this.work); return; }
        const self = this;
        const io = new IntersectionObserver(
          (entries) => {
            entries.forEach((e) => {
              if (e.isIntersecting && !self.userTouched && !self.played) {
                self.played = true;
                io.disconnect();
                self.setVal(self.min);
                self.tween(self.min, self.max, 1100, false, true);
                setTimeout(() => {
                  if (!self.userTouched) self.tween(self.max, self.work, 700, true);
                }, 1180);
              }
            });
          },
          { threshold: 0.4 }
        );
        io.observe(this);
      }
    }
  );
}
